import React from 'react';
import { ScanLine } from 'lucide-react';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { SCAN_SECTIONS, enabledCount, scanSectionsOf } from '../lib/scanSections';

/**
 * The scan sections a vendor has, as chips — the vendors table.
 */
export function ScanSectionsBadges({ vendor }) {
  const sections = scanSectionsOf(vendor);
  return (
    <div className="flex flex-wrap gap-1" data-testid="scan-sections-badges">
      {SCAN_SECTIONS.map(({ key, label, short }) => (
        <Badge
          key={key}
          title={`${label}: ${sections[key] ? 'enabled' : 'disabled'}`}
          className={sections[key]
            ? 'bg-teal-100 text-teal-800 hover:bg-teal-100'
            : 'bg-gray-100 text-gray-400 line-through hover:bg-gray-100'}
        >
          {short}
        </Badge>
      ))}
    </div>
  );
}

/**
 * One switch per scan section, for the super admin on Add / Edit Vendor.
 *
 * `value` is the vendor form; the switches write `value.scanSections`, always
 * as a full map so the stored record says exactly what was chosen.
 */
export default function ScanSectionsFields({ value, onChange, idPrefix = 'sections' }) {
  const sections = scanSectionsOf(value);
  const set = (key, on) => onChange({ ...value, scanSections: { ...sections, [key]: on } });
  const count = enabledCount(sections);

  return (
    <div className="border-t border-gray-200 pt-4 space-y-3" data-testid="scan-sections-fields">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ScanLine size={15} className="text-purple-500" />
          <Label className="text-sm font-semibold text-gray-700">Scan sections</Label>
        </div>
        <Badge className={count ? 'bg-purple-100 text-purple-700 hover:bg-purple-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}>
          {count} of {SCAN_SECTIONS.length} enabled
        </Badge>
      </div>
      <div className="space-y-2">
        {SCAN_SECTIONS.map(({ key, label, description }) => (
          <div key={key} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2">
            <div className="min-w-0">
              <Label htmlFor={`${idPrefix}-${key}`} className="text-sm font-medium text-gray-800">{label}</Label>
              <p className="text-xs text-gray-500">{description}</p>
            </div>
            <Switch
              id={`${idPrefix}-${key}`}
              checked={sections[key]}
              onCheckedChange={on => set(key, on)}
              data-testid={`${idPrefix}-${key}`}
            />
          </div>
        ))}
      </div>
      {count === 0 && (
        <p className="text-xs text-red-600">With every section off, this clinic cannot start a scan on the kiosk.</p>
      )}
      <p className="text-xs text-gray-500">
        The kiosk skips the screens of a section that is off. Changes reach a signed-in kiosk within
        10 minutes, or as soon as the clinic starts a new analysis.
      </p>
    </div>
  );
}
